import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Download, Users, Calendar, Filter, ArrowLeft, Eye, FileSpreadsheet } from "lucide-react";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import type { AssistantTimeEntry } from "@shared/schema";
import { Link } from "wouter";

interface Assistant {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  assistantType: string;
  permissions: Record<string, boolean>;
  hourlyRate: number | null;
  isActive: boolean;
  createdAt: string;
}

interface TimeEntryWithDetails extends AssistantTimeEntry {
  assistantUser?: { id: string; firstName: string | null; lastName: string | null; email: string | null } | null;
  task?: { id: string; title: string } | null;
  goal?: { id: string; title: string } | null;
}

export default function TimeReportsPage() {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedAssistant, setSelectedAssistant] = useState<string>("all");
  const [selectedEntryDetails, setSelectedEntryDetails] = useState<TimeEntryWithDetails | null>(null);

  const { data: assistants = [], isLoading: assistantsLoading } = useQuery<Assistant[]>({
    queryKey: ["/api/assistants"],
  });

  const queryParams = new URLSearchParams();
  if (startDate) queryParams.set("startDate", startDate);
  if (endDate) queryParams.set("endDate", endDate);
  if (selectedAssistant && selectedAssistant !== "all") {
    queryParams.set("assistantUserId", selectedAssistant);
  }
  const queryString = queryParams.toString();

  const { data: entries = [], isLoading: entriesLoading } = useQuery<TimeEntryWithDetails[]>({
    queryKey: ["/api/time-entries/report", queryString],
    queryFn: async () => {
      const url = queryString ? `/api/time-entries/report?${queryString}` : "/api/time-entries/report";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch time entries report");
      return res.json();
    },
  });

  const { data: allTimeEntries = [] } = useQuery<TimeEntryWithDetails[]>({
    queryKey: ["/api/time-entries/report", "all"],
    queryFn: async () => {
      const res = await fetch("/api/time-entries/report", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch all time entries");
      return res.json();
    },
  });

  const stats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    const totalMinutes = allTimeEntries.reduce((sum, entry) => sum + (entry.durationMinutes || 0), 0);
    
    const thisWeekMinutes = allTimeEntries
      .filter(entry => {
        const date = new Date(entry.clockInAt);
        return date >= weekStart && date <= weekEnd;
      })
      .reduce((sum, entry) => sum + (entry.durationMinutes || 0), 0);

    const thisMonthMinutes = allTimeEntries
      .filter(entry => {
        const date = new Date(entry.clockInAt);
        return date >= monthStart && date <= monthEnd;
      })
      .reduce((sum, entry) => sum + (entry.durationMinutes || 0), 0);

    return {
      totalHours: (totalMinutes / 60).toFixed(1),
      thisWeekHours: (thisWeekMinutes / 60).toFixed(1),
      thisMonthHours: (thisMonthMinutes / 60).toFixed(1),
      assistantCount: assistants.length,
    };
  }, [allTimeEntries, assistants]);

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return "—";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getAssistantName = (entry: TimeEntryWithDetails) => {
    if (entry.assistantUser) {
      const { firstName, lastName, email } = entry.assistantUser;
      if (firstName || lastName) {
        return `${firstName || ""} ${lastName || ""}`.trim();
      }
      return email || "Unknown";
    }
    return "Unknown";
  };

  const exportToCSV = () => {
    const headers = ["Assistant Name", "Date", "Clock In", "Clock Out", "Duration (hours)", "Task", "Goal", "Description"];
    const rows = entries.map(entry => [
      getAssistantName(entry),
      format(new Date(entry.clockInAt), "yyyy-MM-dd"),
      format(new Date(entry.clockInAt), "HH:mm"),
      entry.clockOutAt ? format(new Date(entry.clockOutAt), "HH:mm") : "In Progress",
      entry.durationMinutes ? (entry.durationMinutes / 60).toFixed(2) : "0",
      entry.task?.title || "",
      entry.goal?.title || "",
      entry.description || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `time-report-${format(new Date(), "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedAssistant("all");
  };

  if (assistantsLoading) {
    return (
      <div className="p-4 md:p-6 w-full max-w-full space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (assistants.length === 0) {
    return (
      <div className="p-4 md:p-6 w-full max-w-full">
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium">No Assistants</h3>
            <p className="text-muted-foreground mt-2">
              You don't have any assistants yet. Invite assistants from the{" "}
              <Link href="/assistants" className="text-primary underline" data-testid="link-assistants-page">
                Assistants page
              </Link>
              .
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 w-full max-w-full space-y-6">
      <div className="flex items-center gap-4">
        <Link href="/assistants">
          <Button variant="ghost" size="icon" data-testid="button-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold" data-testid="page-title">Time Card Reports</h1>
          <p className="text-muted-foreground">
            View and analyze time entries from your assistants
          </p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="button-export-csv">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="card-total-hours">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Total Hours
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-total-hours">{stats.totalHours}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card data-testid="card-week-hours">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              This Week
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-week-hours">{stats.thisWeekHours}</div>
            <p className="text-xs text-muted-foreground">Hours logged</p>
          </CardContent>
        </Card>

        <Card data-testid="card-month-hours">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              This Month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-month-hours">{stats.thisMonthHours}</div>
            <p className="text-xs text-muted-foreground">Hours logged</p>
          </CardContent>
        </Card>

        <Card data-testid="card-assistants">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Assistants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="stat-assistants">{stats.assistantCount}</div>
            <p className="text-xs text-muted-foreground">Active assistants</p>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="card-filters">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="input-start-date"
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="input-end-date"
              />
            </div>
            <div>
              <Label htmlFor="assistant">Assistant</Label>
              <Select value={selectedAssistant} onValueChange={setSelectedAssistant}>
                <SelectTrigger data-testid="select-assistant">
                  <SelectValue placeholder="Select assistant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="option-all">All Assistants</SelectItem>
                  {assistants.map((assistant) => (
                    <SelectItem
                      key={assistant.id}
                      value={assistant.id}
                      data-testid={`option-assistant-${assistant.id}`}
                    >
                      {assistant.firstName || assistant.lastName
                        ? `${assistant.firstName || ""} ${assistant.lastName || ""}`.trim()
                        : assistant.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} data-testid="button-clear-filters">
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-entries">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Time Entries
              </CardTitle>
              <CardDescription>
                {entries.length} {entries.length === 1 ? "entry" : "entries"} found
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {entriesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12" data-testid="no-entries">
              <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No time entries found for the selected filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Assistant</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Clock In</TableHead>
                    <TableHead>Clock Out</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Task/Goal</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id} data-testid={`row-entry-${entry.id}`}>
                      <TableCell className="font-medium" data-testid={`text-assistant-${entry.id}`}>
                        {getAssistantName(entry)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {format(new Date(entry.clockInAt), "MMM d, yyyy")}
                        </div>
                      </TableCell>
                      <TableCell>{format(new Date(entry.clockInAt), "h:mm a")}</TableCell>
                      <TableCell>
                        {entry.clockOutAt ? (
                          format(new Date(entry.clockOutAt), "h:mm a")
                        ) : (
                          <Badge variant="outline" className="text-green-600" data-testid={`badge-active-${entry.id}`}>
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {entry.clockOutAt ? formatDuration(entry.durationMinutes) : "In Progress"}
                      </TableCell>
                      <TableCell>
                        {entry.task?.title && (
                          <Badge variant="secondary" className="mr-1" data-testid={`badge-task-${entry.id}`}>
                            {entry.task.title.length > 20 ? entry.task.title.substring(0, 20) + "..." : entry.task.title}
                          </Badge>
                        )}
                        {entry.goal?.title && (
                          <Badge variant="outline" data-testid={`badge-goal-${entry.id}`}>
                            {entry.goal.title.length > 20 ? entry.goal.title.substring(0, 20) + "..." : entry.goal.title}
                          </Badge>
                        )}
                        {!entry.task && !entry.goal && <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {entry.description || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedEntryDetails(entry)}
                          data-testid={`button-view-${entry.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedEntryDetails} onOpenChange={(open) => !open && setSelectedEntryDetails(null)}>
        <DialogContent data-testid="dialog-entry-details">
          <DialogHeader>
            <DialogTitle>Time Entry Details</DialogTitle>
            <DialogDescription>
              View complete details for this time entry
            </DialogDescription>
          </DialogHeader>
          {selectedEntryDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Assistant</Label>
                  <p className="font-medium" data-testid="detail-assistant">{getAssistantName(selectedEntryDetails)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p className="font-medium" data-testid="detail-date">
                    {format(new Date(selectedEntryDetails.clockInAt), "MMMM d, yyyy")}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Clock In</Label>
                  <p className="font-medium" data-testid="detail-clock-in">
                    {format(new Date(selectedEntryDetails.clockInAt), "h:mm a")}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Clock Out</Label>
                  <p className="font-medium" data-testid="detail-clock-out">
                    {selectedEntryDetails.clockOutAt
                      ? format(new Date(selectedEntryDetails.clockOutAt), "h:mm a")
                      : "In Progress"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Duration</Label>
                  <p className="font-medium" data-testid="detail-duration">
                    {selectedEntryDetails.clockOutAt
                      ? formatDuration(selectedEntryDetails.durationMinutes)
                      : "In Progress"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Billing Code</Label>
                  <p className="font-medium" data-testid="detail-billing-code">
                    {selectedEntryDetails.billingCode || "—"}
                  </p>
                </div>
              </div>
              {selectedEntryDetails.task && (
                <div>
                  <Label className="text-muted-foreground">Linked Task</Label>
                  <p className="font-medium" data-testid="detail-task">{selectedEntryDetails.task.title}</p>
                </div>
              )}
              {selectedEntryDetails.goal && (
                <div>
                  <Label className="text-muted-foreground">Linked Goal</Label>
                  <p className="font-medium" data-testid="detail-goal">{selectedEntryDetails.goal.title}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p className="font-medium whitespace-pre-wrap" data-testid="detail-description">
                  {selectedEntryDetails.description || "No description provided"}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
